import React, { useEffect, useState } from 'react';
import { Download, RefreshCw, TrendingUp } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { UserSession, AssessmentResult } from '../types';
import questionsData from '../data/questions.json';
import { generatePDF } from '../utils/pdfGenerator';

interface Props {
  session: UserSession;
  onRestart: () => void;
}

const ResultsPage: React.FC<Props> = ({ session, onRestart }) => {
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    calculateResults();
  }, []);

  const calculateResults = () => {
    const principleScores: { [key: string]: number } = {};
    
    // Calculate score for each principle
    questionsData.principles.forEach(principle => {
      const principleQuestions = questionsData.questions.filter(
        q => q.principle === principle.name
      );
      
      let totalScore = 0;
      let maxScore = principleQuestions.length * 100;
      
      principleQuestions.forEach(question => {
        const answer = session.answers.find(a => a.questionId === question.id);
        if (answer) {
          if (answer.value === 'yes') totalScore += 100;
          else if (answer.value === 'partially') totalScore += 50;
          else totalScore += 0;
        }
      });
      
      principleScores[principle.name] = Math.round((totalScore / maxScore) * 100);
    });

    // Calculate overall score
    const overallScore = Math.round(
      Object.values(principleScores).reduce((a, b) => a + b, 0) / 
      Object.keys(principleScores).length
    );

    // Determine risk level
    let riskLevel: 'low' | 'moderate' | 'high';
    if (overallScore >= 70) riskLevel = 'low';
    else if (overallScore >= 50) riskLevel = 'moderate';
    else riskLevel = 'high';

    // Determine maturity level (1-5)
    const maturityLevel = Math.ceil(overallScore / 20);

    // Generate recommendations
    const recommendations = generateRecommendations(principleScores);

    setResult({
      overallScore,
      riskLevel,
      maturityLevel,
      principleScores,
      recommendations,
    });
    
    setLoading(false);
  };

  const generateRecommendations = (scores: { [key: string]: number }): string[] => {
    const recommendations: string[] = [];
    
    Object.entries(scores).forEach(([principle, score]) => {
      if (score < 50) {
        recommendations.push(`Critical improvement needed in ${principle} - Current score: ${score}%`);
      } else if (score < 70) {
        recommendations.push(`Focus on enhancing ${principle} practices - Current score: ${score}%`);
      }
    });

    if (recommendations.length === 0) {
      recommendations.push('Excellent compliance across all principles. Continue monitoring and improving.');
    }

    return recommendations;
  };

  const getRadarData = () => {
    if (!result) return [];
    
    return questionsData.principles.map(principle => ({
      principle: principle.name,
      score: result.principleScores[principle.name] || 0,
      fullMark: 100,
    }));
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'low':
        return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' };
      case 'moderate':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' };
      case 'high':
        return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
    }
  };

  const getMaturityDescription = (level: number) => {
    const descriptions = [
      'Initial - Ad hoc processes',
      'Developing - Basic practices in place',
      'Defined - Documented processes',
      'Managed - Monitored and controlled',
      'Optimizing - Continuous improvement'
    ];
    return descriptions[level - 1] || descriptions[0];
  };

  const handleDownloadPDF = () => {
    if (result) {
      generatePDF(result, session);
    }
  };

  if (loading || !result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-700">Calculating Results...</p>
        </div>
      </div>
    );
  }

  const riskColors = getRiskColor(result.riskLevel);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-8">
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4">
            <span className="gradient-text">Assessment Results</span>
          </h1>
          <p className="text-xl text-gray-600">
            Comprehensive analysis of your AI risk assessment
          </p>
        </div>

        {/* Top Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Overall Risk */}
          <div className={`bg-white rounded-2xl shadow-lg p-8 border-2 ${riskColors.border}`}>
            <div className={`inline-block px-4 py-2 ${riskColors.bg} ${riskColors.text} rounded-full text-sm font-bold mb-4`}>
              Risk Level
            </div>
            <h2 className="text-4xl font-bold mb-2 capitalize">{result.riskLevel}</h2>
            <p className="text-gray-600">Overall Risk Assessment</p>
            <div className="mt-4">
              <div className="progress-bar">
                <div
                  className="h-full rounded"
                  style={{
                    width: `${result.overallScore}%`,
                    background: result.riskLevel === 'low' ? '#10b981' : 
                               result.riskLevel === 'moderate' ? '#f59e0b' : '#ef4444'
                  }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-2">Risk Score: {result.overallScore}/100</p>
            </div>
          </div>

          {/* Maturity Level */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-indigo-300">
            <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-800 rounded-full text-sm font-bold mb-4">
              Maturity Level
            </div>
            <h2 className="text-4xl font-bold mb-2">Level {result.maturityLevel}</h2>
            <p className="text-gray-600">{getMaturityDescription(result.maturityLevel)}</p>
            <div className="flex gap-2 mt-4">
              {[1, 2, 3, 4, 5].map(level => (
                <div
                  key={level}
                  className={`flex-1 h-2 rounded ${
                    level <= result.maturityLevel ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                ></div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {result.maturityLevel < 5 ? 'Moderate compliance with key gaps' : 'Excellent compliance'}
            </p>
          </div>

          {/* Questions Answered */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-purple-300">
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-bold mb-4">
              Completion
            </div>
            <h2 className="text-4xl font-bold mb-2">{questionsData.total_questions}</h2>
            <p className="text-gray-600">Questions Answered</p>
            <div className="flex items-center gap-2 mt-4 text-green-600">
              <TrendingUp className="w-5 h-5" />
              <span className="font-semibold">100% Complete</span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Radar Chart */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold mb-6">Principles Coverage Analysis</h3>
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={getRadarData()}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis
                  dataKey="principle"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#6b7280' }} />
                <Radar
                  name="Score"
                  dataKey="score"
                  stroke="#4f46e5"
                  fill="#4f46e5"
                  fillOpacity={0.5}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Principle Scores */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold mb-6">Principle Scores</h3>
            <div className="space-y-4">
              {Object.entries(result.principleScores).map(([principle, score]) => {
                const principleData = questionsData.principles.find(p => p.name === principle);
                return (
                  <div key={principle}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{principleData?.icon}</span>
                        <span className="font-semibold">{principle}</span>
                      </div>
                      <span className="font-bold text-indigo-600">{score}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${score}%`,
                          background: score >= 70 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444'
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold mb-6">Recommendations</h3>
          <div className="space-y-3">
            {result.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                  {index + 1}
                </div>
                <p className="text-gray-700">{rec}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-center">
          <button onClick={handleDownloadPDF} className="btn-primary flex items-center gap-2">
            <Download className="w-5 h-5" />
            Download PDF Report
          </button>
          <button
            onClick={onRestart}
            className="px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
